import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'
import { useWarnOnUnload } from '../lib/use-warn-on-unload'
import { Button } from './button'

type OpenChange = (open: boolean, details?: DialogPrimitive.Root.ChangeEventDetails) => void

/**
 * #1233 (guard G7): the shared "Discard changes?" guard behind `Dialog`'s and `Drawer`'s `dirty` prop. A close
 * request (Esc, backdrop, the X, any `DialogClose`) while dirty does not close: it shows one inline confirm in the
 * footer area instead. Keep editing puts focus back on the last field; Discard calls `onDiscard` then closes.
 * A save path that closes by setting `open={false}` itself is not asked.
 */
export function useDirtyClose({
  open,
  dirty,
  onOpenChange,
  onDiscard,
}: {
  open: boolean
  dirty?: boolean
  onOpenChange: OpenChange
  onDiscard?: () => void
}) {
  const [confirming, setConfirming] = React.useState(false)
  const lastFocus = React.useRef<HTMLElement | null>(null)
  const restoreFocus = React.useRef(false)
  useWarnOnUnload(open && Boolean(dirty))

  // A closed or clean form has nothing to confirm.
  React.useEffect(() => {
    if (!open || !dirty) setConfirming(false)
  }, [open, dirty])
  React.useEffect(() => {
    if (confirming || !restoreFocus.current) return
    restoreFocus.current = false
    const el = lastFocus.current
    if (el?.isConnected) el.focus()
  }, [confirming])

  const keepEditing = React.useCallback(() => {
    restoreFocus.current = true
    setConfirming(false)
  }, [])
  const discard = React.useCallback(() => {
    setConfirming(false)
    onDiscard?.()
    onOpenChange(false)
  }, [onDiscard, onOpenChange])
  const handleOpenChange = React.useCallback<OpenChange>(
    (next, details) => {
      if (next || !dirty) return onOpenChange(next, details)
      // A second Esc while the confirm shows means "keep editing", never "discard".
      if (confirming) return keepEditing()
      setConfirming(true)
    },
    [dirty, confirming, onOpenChange, keepEditing],
  )
  // Remember the last field the person was in (not the confirm's own buttons).
  const onFocusCapture = React.useCallback((event: React.FocusEvent<HTMLElement>) => {
    const target = event.target as HTMLElement
    if (target.closest('[data-slot="discard-confirm"], [data-slot="dialog-close"], [data-slot="drawer-close"]')) return
    lastFocus.current = target
  }, [])
  return { confirming, keepEditing, discard, handleOpenChange, onFocusCapture }
}

/** The inline "Discard changes? · Discard · Keep editing" row. Not a second modal. */
export function DiscardConfirm({ onDiscard, onKeepEditing }: { onDiscard: () => void; onKeepEditing: () => void }) {
  const keepRef = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    keepRef.current?.focus()
  }, [])
  return (
    <fieldset
      data-slot="discard-confirm"
      aria-label="Discard changes?"
      className="m-0 flex min-w-0 flex-wrap items-center justify-end gap-2 rounded-md border-0 bg-warning-soft px-3 py-2"
    >
      <p aria-live="assertive" className="mr-auto text-sm font-medium text-warning">
        Discard changes?
      </p>
      <Button type="button" variant="destructive" onClick={onDiscard}>
        Discard
      </Button>
      <Button ref={keepRef} type="button" variant="primary" onClick={onKeepEditing}>
        Keep editing
      </Button>
    </fieldset>
  )
}

/**
 * A button that asks the surrounding `Dialog`/`Drawer` to close, through the same dirty guard as Esc and the X.
 * Use it for Cancel: `<DialogClose render={<Button variant="ghost" />}>Cancel</DialogClose>`.
 */
export const DialogClose = DialogPrimitive.Close

/** Centred modal (key sheet, confirm-before-leave). The palette and drawer have their own shells. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  dirty,
  onDiscard,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  children?: React.ReactNode
  /** Actions that stay visible while a long dialog body scrolls. */
  footer?: React.ReactNode
  className?: string
  /** #1233: something typed would be lost. Any close then asks "Discard changes?" first; the tab warns on unload. */
  dirty?: boolean
  /** Runs when the person chooses Discard, before `onOpenChange(false)`. */
  onDiscard?: () => void
}) {
  const guard = useDirtyClose({ open, dirty, onOpenChange, onDiscard })
  return (
    <DialogPrimitive.Root open={open} onOpenChange={guard.handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-50 bg-foreground/25 transition-opacity duration-(--duration-fast) data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <DialogPrimitive.Popup
          data-slot="dialog-popup"
          onFocusCapture={guard.onFocusCapture}
          className={cn(
            'fixed top-[8vh] left-1/2 z-50 flex max-h-[84vh] w-[min(760px,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-border bg-popover p-5 text-popover-foreground shadow-lg outline-none',
            'max-pane:!inset-0 max-pane:!h-dvh max-pane:!max-h-none max-pane:!w-full max-pane:!min-w-0 max-pane:!max-w-full max-pane:!translate-x-0 max-pane:!translate-y-0 max-pane:rounded-none',
            'transition-[opacity,transform] duration-(--duration-fast) data-[ending-style]:scale-[0.98] data-[ending-style]:opacity-0 data-[starting-style]:scale-[0.98] data-[starting-style]:opacity-0',
            className,
          )}
        >
          <div data-slot="dialog-header" className="mb-3 flex min-w-0 max-w-full shrink-0 items-start gap-3">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-xl font-semibold">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-sm text-muted-foreground">{description}</DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close
              data-slot="dialog-close"
              aria-label="Close"
              title="Close (Esc)"
              className="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted max-pane:size-11"
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          <div data-slot="dialog-body" className="min-h-0 w-full min-w-0 max-w-full flex-1 overflow-y-auto overscroll-y-contain">
            {children}
          </div>
          {footer || guard.confirming ? (
            <div
              data-slot="dialog-footer"
              className="mt-3 w-full min-w-0 max-w-full shrink-0 border-t border-border bg-popover pt-3 pb-[env(safe-area-inset-bottom)]"
            >
              {guard.confirming ? <DiscardConfirm onDiscard={guard.discard} onKeepEditing={guard.keepEditing} /> : footer}
            </div>
          ) : null}
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
